import { motion } from "framer-motion";
import { UaeMap } from "./UaeMap";
import { scrollToSection } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const HeroLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden pb-1 ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.25, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const DOMAINS = [
    "Facility Management",
    "Waste Recovery",
    "Oils & UCO",
    "Advanced Materials",
];

export const Hero = () => (
    <section
        id="home"
        data-testid="hero-section"
        className="relative flex min-h-screen flex-col overflow-hidden bg-white"
    >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_70%_42%,rgba(124,191,82,0.22)_0%,rgba(220,235,210,0.5)_45%,transparent_75%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_40%_35%_at_15%_85%,rgba(89,139,59,0.08),transparent_70%)]" />

        <div className="pointer-events-none relative z-10 mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-center px-6 pt-28 md:px-10">
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.35 }}
                className="mb-8 text-[11px] uppercase tracking-[0.34em] text-[#5C6B57] md:text-xs"
                data-testid="hero-overline"
            >
                NOMAS &middot; Leading Solution Integrator &middot; UAE
            </motion.p>

            <h1 className="font-display max-w-[22ch] text-5xl font-light leading-[1.05] tracking-tighter text-[#0E1112] sm:text-6xl lg:text-8xl">
                <HeroLine delay={0.45}>Integrated Services and</HeroLine>
                <HeroLine delay={0.58} className="text-[#C8102E]">
                    Solutions Provider.
                </HeroLine>
            </h1>

            <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                className="mt-8 max-w-lg text-sm leading-relaxed text-[#45543F] md:text-base"
                data-testid="hero-subtext"
            >
                NOMAS is an integrated services and solutions provider —
                facility management, material recovery, international sourcing
                and advanced materials, developed across the UAE and beyond.
            </motion.p>

            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
                className="pointer-events-auto mt-11 flex flex-wrap items-center gap-5"
            >
                <button
                    onClick={() => scrollToSection("#domains")}
                    data-testid="hero-cta-primary"
                    className="rounded-full bg-[#7CBF52] px-9 py-4 text-[13px] font-semibold tracking-[0.14em] text-[#0E1112] uppercase transition-[transform,background-color] duration-400 hover:-translate-y-0.5 hover:bg-[#8fd064]"
                >
                    Explore Our Solutions
                </button>
                <button
                    onClick={() => scrollToSection("#contact")}
                    data-testid="hero-cta-secondary"
                    className="rounded-full border border-[#0E1112]/20 px-9 py-4 text-[13px] font-semibold tracking-[0.14em] text-[#0E1112] uppercase transition-[border-color,background-color,transform] duration-400 hover:-translate-y-0.5 hover:border-[#598B3B]/70 hover:bg-[#598B3B]/5"
                >
                    Talk to an Expert
                </button>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.6 }}
                className="pointer-events-auto mt-12 flex flex-wrap gap-3"
                data-testid="hero-domains"
            >
                {DOMAINS.map((d, i) => (
                    <span
                        key={d}
                        className="flex items-center gap-2.5 rounded-full border border-[#DCE5D4] bg-white/80 px-4 py-2 text-[11px] font-medium tracking-[0.14em] text-[#45543F] uppercase backdrop-blur"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#7CBF52]" />
                        <span className="font-display mr-1 text-[#598B3B]">0{i + 1}</span>
                        {d}
                    </span>
                ))}
            </motion.div>
        </div>

        <div className="pointer-events-none relative z-0 mx-auto w-full max-w-xl px-6 pb-8 md:absolute md:inset-x-0 md:bottom-20 md:left-[26%] md:top-24 md:mx-0 md:flex md:max-w-none md:items-center md:px-0 md:pb-0">
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.4, delay: 0.7, ease: EASE }}
                className="w-full"
            >
                <UaeMap />
            </motion.div>
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.9 }}
            className="relative z-10 flex flex-col items-center gap-3 pb-9"
            data-testid="hero-scroll-indicator"
        >
            <span className="text-[10px] tracking-[0.4em] text-[#8CA093] uppercase">
                Scroll to Explore
            </span>
            <span className="relative block h-12 w-px overflow-hidden bg-[#0E1112]/10">
                <span className="animate-scroll-drop absolute left-0 top-0 h-full w-px bg-[#598B3B]" />
            </span>
        </motion.div>
    </section>
);
